import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const BrandingContext = createContext(null);

// Raw backend origin — no /api/v1 suffix
const BACKEND_ORIGIN = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/v1\/?$/, '').replace(/\/$/, '');

export const DEFAULT_PUBLIC_LOGO = '/logo.png';
export const DEFAULT_PUBLIC_FAVICON = '/favicon.png';

/**
 * Builds a full URL from a stored branding path.
 * - Super Admin Settings logo/favicon: `/public/branding/logo` → `/api/v1/public/branding/logo`
 * - System uploads: `/api/uploads/branding/...` → same path on backend origin
 */
export function buildBrandingUrl(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  if (path.startsWith('/public/')) {
    return `${BACKEND_ORIGIN}/api/v1${path}`;
  }
  if (path.startsWith('/api/')) {
    return `${BACKEND_ORIGIN}${path}`;
  }
  return `${BACKEND_ORIGIN}${path.startsWith('/') ? '' : '/'}${path}`;
}

export function brandLogoSrc(branding, { mobile = false } = {}) {
  // Web/landing: Super Admin Settings branding logo first (Settings → Branding upload).
  // Mobile/admin sidebar: prefer mobile logo, then fall back to web branding.
  const candidates = mobile
    ? [
        branding?.mobile_logo_url,
        branding?.app_logo_mobile,
        branding?.app_logo,
        branding?.logo_url,
      ]
    : [
        branding?.logo_url,
        branding?.app_logo,
        branding?.mobile_logo_url,
        branding?.app_logo_mobile,
      ];
  const path = candidates.find((value) => String(value || '').trim());
  if (!path) return DEFAULT_PUBLIC_LOGO;
  const url = buildBrandingUrl(path);
  const rev = branding?.rev ?? branding?.updated_at ?? branding?.logo_rev;
  if (rev == null || rev === '') return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${encodeURIComponent(String(rev))}`;
}

export function brandFaviconSrc(branding) {
  const path = branding?.favicon_url || branding?.app_favicon;
  return path ? buildBrandingUrl(path) : DEFAULT_PUBLIC_FAVICON;
}

const PRODUCT_NAME = 'DietCoach';
const LEGACY_PRODUCT_NAME = /^wings\s*diet(\s*b2b)?$/i;

function normalizeProductName(value) {
  const name = String(value || '').trim();
  // Empty means SuperAdmin has not set a brand name — do not invent a default for UI.
  if (!name) return '';
  if (LEGACY_PRODUCT_NAME.test(name)) return PRODUCT_NAME;
  return name;
}

const DEFAULT_BRANDING = {
  brand_name: '',
  slogan: '',
  company_name: '',
  website: '',
  admin_panel_url: '',
  logo_url: '',
  favicon_url: '',
  mobile_logo_url: '',
  app_logo: '',
  app_logo_mobile: '',
  app_favicon: '',
  landing_theme: { preset: 'light', custom: {} },
};

export const PUBLIC_BRANDING_CACHE_KEY = 'public_branding_cache';

export function readPublicBrandingCache() {
  try {
    const raw = localStorage.getItem(PUBLIC_BRANDING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const next = { ...DEFAULT_BRANDING, ...parsed };
    next.brand_name = normalizeProductName(next.brand_name);
    return next;
  } catch {
    return null;
  }
}

export function cachedPublicLogoSrc() {
  const cached = readPublicBrandingCache();
  return cached ? brandLogoSrc(cached) : DEFAULT_PUBLIC_LOGO;
}

function applyFavicon(href) {
  if (!href) return;
  const head = document.head;
  let link = head.querySelector('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    head.appendChild(link);
  }
  link.href = href;

  let apple = head.querySelector('link[rel="apple-touch-icon"]');
  if (!apple) {
    apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    head.appendChild(apple);
  }
  apple.href = href === DEFAULT_PUBLIC_FAVICON || href.endsWith('/favicon.png')
    ? '/apple-touch-icon.png'
    : href;
}

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(() => readPublicBrandingCache() || DEFAULT_BRANDING);
  const [loading, setLoading] = useState(() => !readPublicBrandingCache());

  const refresh = async () => {
    const hadCache = Boolean(readPublicBrandingCache());
    if (!hadCache) setLoading(true);
    try {
      const hostname = encodeURIComponent(window.location.hostname);
      const res = await fetch(`${BACKEND_ORIGIN}/api/v1/public/branding?hostname=${hostname}`, { cache: 'no-store' });
      const data = await res.json();
      const next = { ...DEFAULT_BRANDING, ...(data || {}) };
      next.brand_name = normalizeProductName(next.brand_name);
      if (LEGACY_PRODUCT_NAME.test(String(next.app_name || '').trim())) {
        next.app_name = PRODUCT_NAME;
      }
      setBranding(next);
      try {
        localStorage.setItem(PUBLIC_BRANDING_CACHE_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota */
      }

      // Page title = App Name only
      document.title = next.brand_name || PRODUCT_NAME;
      applyFavicon(brandFaviconSrc(next));
    } catch {
      if (!hadCache) {
        setBranding(DEFAULT_BRANDING);
        document.title = PRODUCT_NAME;
        applyFavicon(DEFAULT_PUBLIC_FAVICON);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cached = readPublicBrandingCache();
    applyFavicon(cached ? brandFaviconSrc(cached) : DEFAULT_PUBLIC_FAVICON);
    if (cached?.brand_name) document.title = cached.brand_name;
    let mounted = true;
    const run = async () => { if (mounted) await refresh(); };
    run();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ branding, setBranding, loading, refresh }), [branding, loading]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};

export const useBranding = () => {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
};
